import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';

export interface IncomingMessage {
    id: string;
    conversationId: string;
    senderId: string;
    senderName: string;
    content: string;
    messageType: string;
    attachmentUrl?: string;
    sentAt: string;
}

export interface PresenceEvent {
    userId: string;
    isOnline: boolean;
    lastSeenAt?: string;
}

export interface TypingEvent {
    conversationId: string;
    userId: string;
    typing: boolean;
}

/**
 * Thin wrapper around the SignalR `/hubs/chat` connection.
 * Auth flows from the same HttpOnly cookie used by the REST API (withCredentials).
 */
@Injectable({ providedIn: 'root' })
export class ChatSignalRService {
    private _hub: signalR.HubConnection | null = null;

    readonly message$ = new Subject<IncomingMessage>();
    readonly presence$ = new Subject<PresenceEvent>();
    readonly onlineUsers$ = new Subject<string[]>();
    readonly typing$ = new Subject<TypingEvent>();

    get connected(): boolean {
        return this._hub?.state === signalR.HubConnectionState.Connected;
    }

    /** Build (once) and start the connection. Idempotent. */
    async start(): Promise<void> {
        if (this._hub && this._hub.state !== signalR.HubConnectionState.Disconnected) {
            return;
        }

        if (!this._hub) {
            this._hub = new signalR.HubConnectionBuilder()
                .withUrl(`${environment.baseUrl}/hubs/chat`, {
                    withCredentials: true,
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            this._hub.on('ReceiveMessage', (m: IncomingMessage) => this.message$.next(m));
            this._hub.on('PresenceChanged', (p: PresenceEvent) => this.presence$.next(p));
            this._hub.on('OnlineUsers', (ids: string[]) => this.onlineUsers$.next(ids || []));
            this._hub.on('Typing', (e: { conversationId: string; userId: string }) =>
                this.typing$.next({ ...e, typing: true })
            );
            this._hub.on('StopTyping', (e: { conversationId: string; userId: string }) =>
                this.typing$.next({ ...e, typing: false })
            );
        }

        try {
            await this._hub.start();
        } catch (error) {
            console.error('SignalR chat connection failed', error);
        }
    }

    async stop(): Promise<void> {
        if (this._hub) {
            try {
                await this._hub.stop();
            } catch {
                /* ignore */
            }
        }
    }

    async sendMessage(conversationId: string, content: string): Promise<void> {
        if (this.connected) {
            await this._hub!.invoke('SendMessage', conversationId, content);
        }
    }

    async markRead(conversationId: string): Promise<void> {
        if (this.connected) {
            try {
                await this._hub!.invoke('MarkRead', conversationId);
            } catch {
                /* ignore */
            }
        }
    }

    async typing(conversationId: string): Promise<void> {
        if (this.connected) {
            try {
                await this._hub!.invoke('Typing', conversationId);
            } catch {
                /* ignore */
            }
        }
    }

    async stopTyping(conversationId: string): Promise<void> {
        if (this.connected) {
            try {
                await this._hub!.invoke('StopTyping', conversationId);
            } catch {
                /* ignore */
            }
        }
    }
}
